/*
 * Authenticated provider dashboard Requires providerId Contains tabs:
 * ProviderInformation Waitlist, Appointments, Messages
 * This is where providers land after claim
 */
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import WaitlistTab from "./WaitlistTab";
import ActivePatientsTab from "./ActivePatientsTab";
import ProviderAppointmentsTab from "./ProviderAppointmentsTab";
import MessagesTab from "./MessagesTab";
import NotificationsTab from "./NotificationsTab";
import ProviderInformation from "./ProviderInformation";
import ReferralsSentTab from "./ReferralsSentTab";
import ReferralsReceivedTab from "./ReferralsReceivedTab";
import ReferralIntakeModal from "./ReferralIntakeModal";
import { useNavigate } from "react-router-dom";
import { Stethoscope, Camera, UserRound } from "lucide-react";
import AvailabilitiesTab from "./AvailabilitiesTab";
import CredentialsTab from "./CredentialsTab";
import PricingTab from "./PricingTab";
import BillingTab from "./BillingTab";
import PremiumGate from "./PremiumGate";
import SubscriptionTrialModal from "./SubscriptionTrialModal";
import {
  getProvider,
  uploadProviderProfilePicture,
  deleteProviderProfilePicture,
  updateAcceptingNewPatients,
  createSubscriptionSession,
  verifySubscriptionSession,
} from "../APIs/providerApi";
import { getProviderRatings } from "../APIs/ratingsApi";
import { useLocation } from "react-router-dom";
import keycloak from "../APIs/keycloak";
import { useCurrentUser } from "../APIs/UserContext";
import { showToast } from "../APIs/utils/toastManager";

const tabs = [
  "provider profile",
  "credentials",
  "waitlist",
  "active patients",
  "appointments",
  "messages",
  "referrals",
  "notifications",
  "availability",
  "pricing",
  "billing",
];

const GATED_TABS = [
  "waitlist",
  "active patients",
  "appointments",
  "messages",
  "referrals",
  "notifications",
  "availability",
];
const STRIPE_SUBSCRIPTION_SESSION_KEY =
  "medmaze_pending_subscription_session_id";

export default function ProviderProfile() {
  const location = useLocation();

  const { currentUser } = useCurrentUser();
  const providerId = currentUser?.providerId ?? location.state?.providerId;

  const [activeTab, setActiveTab] = useState("provider profile");
  const navigate = useNavigate();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [providerInfo, setProviderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referralSubTab, setReferralSubTab] = useState("sent");
  const [showReferralIntake, setShowReferralIntake] = useState(false);
  const [referralRefreshKey, setReferralRefreshKey] = useState(0);
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);
  const [pendingStripeSessionId, setPendingStripeSessionId] = useState(null);

  // Sidebar state
  const [accepting, setAccepting] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [premiumEnabled, setPremiumEnabled] = useState(true);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);

  // Photo modal state
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const photoInputRef = useRef(null);
  const verifyingStripeSessionRef = useRef(null);

  const handleLogout = () => {
    setDropdownOpen(false);
    localStorage.removeItem("medmaze_signup_role_locked");
    localStorage.removeItem("medmaze_signup_role_pending");
    localStorage.removeItem("medmaze_pending_claim_npi");
    keycloak.logout({
      redirectUri: window.location.origin + "/ProviderSearchPage",
    });
  };

  useEffect(() => {
    const fetchProvider = async () => {
      if (!providerId) {
        console.error("No providerId provided");
        setLoading(false);
        return;
      }
      try {
        const data = await getProvider(providerId);
        setProviderInfo({
          name: `${data.firstName} ${data.lastName}`,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          specialty: data.specialties?.map((s) => s.name).join(", ") || "N/A",
          specialties: data.specialties || [],
          fullData: data,
        });
        setAccepting(data.acceptingNewPatients ?? false);
        setProfilePictureUrl(data.profilePictureUrl ?? null);
        setPremiumEnabled(data.premiumEnabled ?? true);
        const params = new URLSearchParams(window.location.search);
        if (params.get("justClaimed") === "1") {
          setShowTrialModal(true);
          window.history.replaceState({}, "", window.location.pathname);
        }
      } catch (err) {
        console.error("Error fetching provider:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [providerId]);

  useEffect(() => {
    if (!providerId) return;
    getProviderRatings(providerId)
      .then((data) => {
        setAverageRating(data.averageRating || 0);
        setReviewCount(data.reviewCount || 0);
      })
      .catch(() => {});
  }, [providerId]);

  // Step 1: Detect return from Stripe and persist session ID across Strict Mode remounts.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isBillingReturn = params.get("tab") === "billing";
    const sessionId = params.get("session_id");

    if (isBillingReturn) {
      setActiveTab("billing");

      if (params.get("status") === "success" && sessionId) {
        sessionStorage.setItem(STRIPE_SUBSCRIPTION_SESSION_KEY, sessionId);
        setPendingStripeSessionId(sessionId);
      } else {
        setBillingRefreshKey((k) => k + 1);
        window.history.replaceState({}, "", window.location.pathname);
      }
      return;
    }

    const storedSessionId = sessionStorage.getItem(
      STRIPE_SUBSCRIPTION_SESSION_KEY,
    );
    if (storedSessionId) {
      setActiveTab("billing");
      setPendingStripeSessionId(storedSessionId);
    }
  }, [location.search]);

  // Step 2: Once providerId is loaded, call verify with the saved session ID
  useEffect(() => {
    if (!pendingStripeSessionId || !providerId) return;
    if (verifyingStripeSessionRef.current === pendingStripeSessionId) return;
    verifyingStripeSessionRef.current = pendingStripeSessionId;

    verifySubscriptionSession(pendingStripeSessionId)
      .then(() => setBillingRefreshKey((k) => k + 1))
      .catch(() => setBillingRefreshKey((k) => k + 1))
      .finally(() => {
        verifyingStripeSessionRef.current = null;
        sessionStorage.removeItem(STRIPE_SUBSCRIPTION_SESSION_KEY);
        setPendingStripeSessionId(null);
        window.history.replaceState({}, "", window.location.pathname);
      });
  }, [pendingStripeSessionId, providerId]);

  const handleToggleAccepting = async () => {
    const newVal = !accepting;
    setAccepting(newVal); // optimistic
    try {
      await updateAcceptingNewPatients(providerId, newVal);
    } catch (e) {
      console.error("Failed to update accepting status:", e);
      setAccepting(!newVal); // revert on error
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setUploadError("Image must be 50 MB or smaller.");
      e.target.value = "";
      return;
    }
    setUploadError(null);
    setPreviewFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handlePhotoSave = async () => {
    if (!previewFile) return;
    setUploadingPhoto(true);
    setUploadError(null);
    try {
      await uploadProviderProfilePicture(providerId, previewFile);
      const data = await getProvider(providerId);
      setProfilePictureUrl(data.profilePictureUrl ?? null);
      setShowPhotoModal(false);
      setPreviewFile(null);
      setPreviewUrl(null);
      showToast("Profile photo updated!", "success");
    } catch (e) {
      console.error("Failed to upload photo:", e);
      const msg =
        e?.response?.data?.detail ??
        e?.response?.data?.message ??
        e?.response?.data ??
        "Upload failed. Please try again.";
      setUploadError(
        typeof msg === "string" ? msg : "Upload failed. Please try again.",
      );
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoRemove = async () => {
    setUploadingPhoto(true);
    try {
      await deleteProviderProfilePicture(providerId);
      setProfilePictureUrl(null);
      setShowPhotoModal(false);
      setPreviewFile(null);
      setPreviewUrl(null);
    } catch (e) {
      console.error("Failed to remove photo:", e);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setPreviewFile(null);
    setUploadError(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span
        key={i}
        className={i < Math.round(rating) ? "text-yellow-400" : "text-gray-300"}
      >
        ★
      </span>
    ));
  };

  const handleSubscribeRedirect = async () => {
    try {
      const { checkoutUrl } = await createSubscriptionSession();
      window.location.href = checkoutUrl;
    } catch {
      showToast("Failed to start checkout. Please try again.", "error");
    }
  };

  const renderTabComponent = (tab) => {
    if (GATED_TABS.includes(tab) && !premiumEnabled) {
      return <PremiumGate onSubscribe={handleSubscribeRedirect} />;
    }

    switch (tab) {
      case "provider profile":
        return <ProviderInformation providerId={providerId} />;
      case "credentials":
        return <CredentialsTab providerId={providerId} />;
      case "waitlist":
        return <WaitlistTab providerId={providerId} />;
      case "active patients":
        return <ActivePatientsTab providerId={providerId} />;
      case "appointments":
        return <ProviderAppointmentsTab providerId={providerId} />;
      case "messages":
        return <MessagesTab providerId={providerId} />;
      case "referrals":
        return (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setReferralSubTab("sent")}
                  className={`px-4 py-1.5 text-sm rounded ${referralSubTab === "sent" ? "bg-teal-600 text-white" : "bg-gray-200 text-gray-700"}`}
                >
                  Sent
                </button>
                <button
                  onClick={() => setReferralSubTab("received")}
                  className={`px-4 py-1.5 text-sm rounded ${referralSubTab === "received" ? "bg-teal-600 text-white" : "bg-gray-200 text-gray-700"}`}
                >
                  Received
                </button>
                <button
                  onClick={() => setReferralSubTab("accepted")}
                  className={`px-4 py-1.5 text-sm rounded ${referralSubTab === "accepted" ? "bg-teal-600 text-white" : "bg-gray-200 text-gray-700"}`}
                >
                  Accepted
                </button>
                <button
                  onClick={() => setReferralSubTab("completed")}
                  className={`px-4 py-1.5 text-sm rounded ${referralSubTab === "completed" ? "bg-teal-600 text-white" : "bg-gray-200 text-gray-700"}`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setReferralSubTab("declined")}
                  className={`text-sm px-3 py-1 rounded-full ${
                    referralSubTab === "declined"
                      ? "bg-red-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Declined
                </button>
                <button
                  onClick={() => setReferralSubTab("cancelled")}
                  className={`text-sm px-3 py-1 rounded-full ${
                    referralSubTab === "cancelled"
                      ? "bg-red-700 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Cancelled
                </button>
              </div>
              <button
                onClick={() => setShowReferralIntake(true)}
                className="px-4 py-1.5 text-sm rounded bg-teal-600 text-white"
              >
                + New Referral
              </button>
            </div>
            {referralSubTab === "sent" && (
              <ReferralsSentTab
                key={referralRefreshKey}
                providerId={providerId}
              />
            )}
            {referralSubTab === "received" && (
              <ReferralsReceivedTab
                key={referralRefreshKey}
                providerId={providerId}
                statusFilter={["PENDING_ACCEPTANCE", "INFO_REQUESTED"]}
              />
            )}
            {referralSubTab === "accepted" && (
              <ReferralsReceivedTab
                key={referralRefreshKey}
                providerId={providerId}
                statusFilter={[
                  "ACCEPTED",
                  "MANUAL_REVIEW",
                  "APPOINTMENT_OFFERED",
                  "SCHEDULED",
                ]}
              />
            )}
            {referralSubTab === "completed" && (
              <ReferralsReceivedTab
                key={referralRefreshKey}
                providerId={providerId}
                statusFilter={["COMPLETED"]}
              />
            )}
            {referralSubTab === "declined" && (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    Declined by Specialist
                  </p>
                  <ReferralsSentTab
                    key={referralRefreshKey}
                    providerId={providerId}
                    declinedView={true}
                    onResend={() => setShowReferralIntake(true)}
                  />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    Declined by You
                  </p>
                  <ReferralsReceivedTab
                    key={referralRefreshKey}
                    providerId={providerId}
                    declinedByMe={true}
                  />
                </div>
              </div>
            )}
            {referralSubTab === "cancelled" && (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    Cancelled by You
                  </p>
                  <ReferralsSentTab
                    key={referralRefreshKey}
                    providerId={providerId}
                    cancelledView={true}
                  />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    Cancelled by Referring Provider
                  </p>
                  <ReferralsReceivedTab
                    key={referralRefreshKey}
                    providerId={providerId}
                    cancelledByPcp={true}
                  />
                </div>
              </div>
            )}
          </div>
        );
      case "notifications":
        return <NotificationsTab providerId={providerId} />;
      case "availability":
        return <AvailabilitiesTab providerId={providerId} />;
      case "pricing":
        return <PricingTab providerId={providerId} />;
      case "billing":
        return (
          <BillingTab providerId={providerId} refreshKey={billingRefreshKey} />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading provider profile...</p>
      </div>
    );
  }

  if (!providerInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Provider not found.</p>
      </div>
    );
  }

  const initials = `${providerInfo.firstName?.[0] ?? ""}${providerInfo.lastName?.[0] ?? ""}`;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SubscriptionTrialModal
        isOpen={showTrialModal}
        onClose={() => setShowTrialModal(false)}
      />

      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <Stethoscope className="text-teal-600" size={26} />
          <span className="text-xl font-bold text-gray-900">MedMaze</span>
        </div>

        {/* Initials dropdown */}
        <div className="relative">
          <button
            onClick={() => setDropdownOpen(!isDropdownOpen)}
            className="h-9 w-9 rounded-full bg-teal-600 text-white font-bold text-sm flex items-center justify-center hover:bg-teal-700 transition-colors"
          >
            {initials}
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
              <button
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                onClick={() => {
                  setDropdownOpen(false);
                  navigate("/provider/edit-profile");
                }}
              >
                Edit Profile
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                onClick={() => {
                  setDropdownOpen(false);
                  navigate("/provider/settings");
                }}
              >
                Settings
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-red-600 border-t border-gray-100 hover:bg-red-50"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 max-w-7xl mx-auto w-full gap-6 px-6 py-6">
        {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
        <aside className="w-64 shrink-0">
          <div className="rounded-2xl border border-teal-300 bg-white shadow-sm p-5 sticky top-6 space-y-4">
            {/* Avatar */}
            <div className="flex flex-col items-center gap-2">
              <div
                className="relative group cursor-pointer"
                onClick={() => setShowPhotoModal(true)}
              >
                {profilePictureUrl ? (
                  <img
                    src={profilePictureUrl}
                    alt="Profile"
                    className="h-20 w-20 rounded-full object-cover border-2 border-teal-200"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                    <UserRound size={44} className="text-gray-400" />
                  </div>
                )}
                <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera size={20} className="text-white" />
                </div>
              </div>
              {!profilePictureUrl && (
                <button
                  type="button"
                  onClick={() => setShowPhotoModal(true)}
                  className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                >
                  Upload Profile Picture
                </button>
              )}
            </div>

            {/* Name */}
            <div className="text-center">
              <p className="text-base font-bold text-gray-900">
                Dr. {providerInfo.name}
              </p>
            </div>

            {/* Specialty pills */}
            {providerInfo.specialties.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1.5">
                {providerInfo.specialties.map((s, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-full bg-teal-50 border border-teal-200 px-2.5 py-0.5 text-xs font-semibold text-teal-700"
                  >
                    {s.name}
                  </span>
                ))}
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* Accepting New Patients toggle */}
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600">
                  Accepting New Patients
                </span>
                <button
                  type="button"
                  onClick={handleToggleAccepting}
                  className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors duration-200 focus:outline-none ${accepting ? "bg-teal-600" : "bg-gray-300"}`}
                  role="switch"
                  aria-checked={accepting}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 mt-0.5 ${accepting ? "translate-x-5" : "translate-x-0.5"}`}
                  />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {accepting ? "Visible to patients" : "Hidden from new patients"}
              </p>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* Rating */}
            <div className="text-center">
              <div className="flex justify-center text-lg">
                {renderStars(averageRating)}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {reviewCount > 0
                  ? `${averageRating.toFixed(1)} / 5 · ${reviewCount} review${reviewCount !== 1 ? "s" : ""}`
                  : "No ratings yet"}
              </p>
            </div>
          </div>
        </aside>

        {/* ── Main ─────────────────────────────────────────────────────────────── */}
        <main className="flex-1 flex flex-col min-w-0">
          {/* Tab strip */}
          <div className="bg-white border border-gray-200 rounded-2xl mb-4 px-2 py-1 flex gap-1 flex-wrap">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize whitespace-nowrap transition-colors ${
                  activeTab === tab
                    ? "bg-teal-600 text-white"
                    : "text-gray-600 hover:bg-teal-50 hover:text-teal-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-200 p-6 overflow-auto">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {renderTabComponent(activeTab)}
            </motion.div>
          </div>
        </main>
      </div>

      {/* ── Photo Preview Modal ──────────────────────────────────────────────── */}
      {showPhotoModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-900">
                Profile Photo
              </h2>
              <button
                type="button"
                onClick={closePhotoModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5 flex flex-col items-center gap-4">
              {/* Preview */}
              <div className="h-32 w-32 rounded-full overflow-hidden bg-teal-700 flex items-center justify-center border-2 border-teal-200">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="h-full w-full object-cover"
                  />
                ) : profilePictureUrl ? (
                  <img
                    src={profilePictureUrl}
                    alt="Current"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-white text-3xl font-bold">
                    {initials}
                  </span>
                )}
              </div>

              {/* Hidden file input */}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />

              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="px-4 py-2 rounded-full border border-teal-600 text-teal-600 hover:bg-teal-50 text-sm font-semibold transition-colors"
              >
                {previewFile ? "Change Selection" : "Choose Photo"}
              </button>

              {previewFile && (
                <p className="text-xs text-gray-500 text-center">
                  {previewFile.name}
                </p>
              )}

              {uploadError && (
                <p className="text-xs text-red-600 text-center">
                  {uploadError}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between px-6 pb-5 gap-2">
              {profilePictureUrl && !previewFile && (
                <button
                  type="button"
                  onClick={handlePhotoRemove}
                  disabled={uploadingPhoto}
                  className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                >
                  {uploadingPhoto ? "Removing…" : "Remove Photo"}
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  type="button"
                  onClick={closePhotoModal}
                  className="px-4 py-2 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                {previewFile && (
                  <button
                    type="button"
                    onClick={handlePhotoSave}
                    disabled={uploadingPhoto}
                    className="px-4 py-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    {uploadingPhoto ? "Saving…" : "Save"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Referral Intake Modal ─────────────────────────────────────────────── */}
      {showReferralIntake && (
        <ReferralIntakeModal
          providerId={providerId}
          onClose={() => setShowReferralIntake(false)}
          onSubmitted={() => {
            setActiveTab("referrals");
            setReferralSubTab("sent");
            setReferralRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
